'use client';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { updateProfile, changePassword, clearAuthError, fetchEmailNotifications, updateEmailNotifications, fetchVacations, createVacation, updateVacation, deleteVacation } from '../redux/actions/authActions';

const GeneralSchema = Yup.object().shape({
  firstName: Yup.string().required('Le prénom est requis'),
  lastName: Yup.string().required('Le nom est requis'),
  emergencyContact: Yup.string()
});

const CompanySchema = Yup.object().shape({
  companyName: Yup.string().required("Nom de l'entreprise requis"),
  companySiret: Yup.string().required('SIRET requis'),
  companyAddress: Yup.string().required('Adresse requise'),
  companyPhone: Yup.string().required('Téléphone requis')
});

const PasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Le mot de passe actuel est requis'),
  newPassword: Yup.string()
    .required('Le nouveau mot de passe est requis')
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Les mots de passe doivent correspondre')
    .required('La confirmation du mot de passe est requise')
});

const Profile = () => {
  const dispatch = useDispatch();
  const { user, loading, error, message, emailNotifications, emailNotifLoading, emailNotifSaving, vacations, vacationsLoading, vacationSaving, vacationError } = useSelector(state => state.auth);

  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });
  const [activeTab, setActiveTab] = useState('general');
  const [localNotifs, setLocalNotifs] = useState(null);
  const [notifSaveSuccess, setNotifSaveSuccess] = useState(false);
  const [vacationForm, setVacationForm] = useState({ startDate: '', endDate: '', label: '' });
  const [editingVacation, setEditingVacation] = useState(null);
  const [vacationFormError, setVacationFormError] = useState('');
  const [vacationSuccess, setVacationSuccess] = useState('');

  useEffect(() => {
    dispatch(clearAuthError());
    dispatch(fetchEmailNotifications());
    dispatch(fetchVacations());
  }, [dispatch]);

  useEffect(() => {
    if (emailNotifications !== undefined) {
      setLocalNotifs(emailNotifications);
    }
  }, [emailNotifications]);

  const handleGeneralSubmit = async (values, { setSubmitting }) => {
    try {
      await dispatch(updateProfile(values));
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompanySubmit = async (values, { setSubmitting }) => {
    try {
      await dispatch(updateProfile(values));
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await dispatch(changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      }));
      resetForm();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleShowPassword = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getNotifPref = (key) => {
    const defaults = {
      submission_status: { enabled: true, statuses: ['approved', 'rejected', 'in_progress'] },
      order_status: { enabled: true, statuses: ['accepted', 'in_progress', 'completed', 'sent', 'cancelled'] }
    };
    return localNotifs?.[key] ?? defaults[key];
  };

  const toggleNotifEnabled = (key) => {
    setLocalNotifs(prev => ({
      ...prev,
      [key]: { ...getNotifPref(key), enabled: !getNotifPref(key).enabled }
    }));
    setNotifSaveSuccess(false);
  };

  const toggleNotifStatus = (key, status) => {
    const current = getNotifPref(key);
    const statuses = current.statuses || [];
    const next = statuses.includes(status) ? statuses.filter(s => s !== status) : [...statuses, status];
    setLocalNotifs(prev => ({
      ...prev,
      [key]: { ...current, statuses: next }
    }));
    setNotifSaveSuccess(false);
  };

  const handleSaveNotifications = async () => {
    await dispatch(updateEmailNotifications(localNotifs));
    setNotifSaveSuccess(true);
    setTimeout(() => setNotifSaveSuccess(false), 3000);
  };

  const handleVacationSubmit = async (e) => {
    e.preventDefault();
    setVacationFormError('');
    setVacationSuccess('');
    const data = editingVacation
      ? { startDate: vacationForm.startDate, endDate: vacationForm.endDate, label: vacationForm.label }
      : vacationForm;
    try {
      if (editingVacation) {
        await dispatch(updateVacation(editingVacation.id, data));
        setVacationSuccess('Période modifiée avec succès.');
      } else {
        await dispatch(createVacation(data));
        setVacationSuccess('Période ajoutée avec succès.');
      }
      setVacationForm({ startDate: '', endDate: '', label: '' });
      setEditingVacation(null);
      setTimeout(() => setVacationSuccess(''), 3000);
    } catch (err) {
      setVacationFormError(err.response?.data?.message || 'Une erreur est survenue.');
    }
  };

  const handleEditVacation = (v) => {
    setEditingVacation(v);
    setVacationForm({ startDate: v.start_date, endDate: v.end_date, label: v.label || '' });
    setVacationFormError('');
    setVacationSuccess('');
  };

  const handleDeleteVacation = async (id) => {
    if (!window.confirm('Supprimer cette période de vacances ?')) return;
    try {
      await dispatch(deleteVacation(id));
      setVacationSuccess('Période supprimée.');
      setTimeout(() => setVacationSuccess(''), 3000);
    } catch (err) {
      setVacationFormError(err.response?.data?.message || 'Une erreur est survenue.');
    }
  };

  const cancelEdit = () => {
    setEditingVacation(null);
    setVacationForm({ startDate: '', endDate: '', label: '' });
    setVacationFormError('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  if (!user) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des informations utilisateur...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="page-header">
        <h1>Mon profil</h1>
      </div>

      <div className="profile-tabs">
        <button
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          Général
        </button>
        <button
          className={`tab-button ${activeTab === 'company' ? 'active' : ''}`}
          onClick={() => setActiveTab('company')}
        >
          Entreprise
        </button>
        <button
          className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          Mot de passe
        </button>
        <button
          className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        <button
          className={`tab-button ${activeTab === 'vacations' ? 'active' : ''}`}
          onClick={() => setActiveTab('vacations')}
        >
          Vacances
        </button>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="profile-content">

        {/* ── Onglet Général ── */}
        {activeTab === 'general' && (
          <div className="profile-card">
            <div className="profile-header">
              <div className="user-avatar large">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <div className="profile-info">
                <h2>{user.firstName} {user.lastName}</h2>
                <p>{user.email}</p>
                <span className={`role-badge ${user.role === 'admin' ? 'admin' : 'user'}`}>
                  {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                </span>
              </div>
            </div>

            <Formik
              initialValues={{
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                emergencyContact: user.emergencyContact || ''
              }}
              validationSchema={GeneralSchema}
              onSubmit={handleGeneralSubmit}
              enableReinitialize
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="profile-form">
                  <div className="form-group">
                    <label htmlFor="firstName">Prénom</label>
                    <Field
                      type="text"
                      name="firstName"
                      id="firstName"
                      className={`form-control ${errors.firstName && touched.firstName ? 'is-invalid' : ''}`}
                    />
                    <ErrorMessage name="firstName" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Nom</label>
                    <Field
                      type="text"
                      name="lastName"
                      id="lastName"
                      className={`form-control ${errors.lastName && touched.lastName ? 'is-invalid' : ''}`}
                    />
                    <ErrorMessage name="lastName" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={user.email}
                      className="form-control"
                      disabled
                    />
                    <small className="form-text text-muted">L'email ne peut pas être modifié</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="emergencyContact">Numéro de contact d'urgence</label>
                    <Field
                      type="text"
                      name="emergencyContact"
                      id="emergencyContact"
                      className="form-control"
                      placeholder="+33 6 00 00 00 00"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting || loading ? 'Enregistrement...' : 'Mettre à jour'}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        )}

        {/* ── Onglet Entreprise ── */}
        {activeTab === 'company' && (
          <div className="profile-card">
            <Formik
              initialValues={{
                companyName: user.companyName || '',
                companySiret: user.companySiret || '',
                companyAddress: user.companyAddress || '',
                companyPhone: user.companyPhone || ''
              }}
              validationSchema={CompanySchema}
              onSubmit={handleCompanySubmit}
              enableReinitialize
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="profile-form">
                  <div className="form-group">
                    <label htmlFor="companyName">Nom de l'entreprise *</label>
                    <Field
                      type="text"
                      name="companyName"
                      id="companyName"
                      className={`form-control ${errors.companyName && touched.companyName ? 'is-invalid' : ''}`}
                      placeholder="Nom de votre entreprise"
                    />
                    <ErrorMessage name="companyName" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="companySiret">SIRET *</label>
                    <Field
                      type="text"
                      name="companySiret"
                      id="companySiret"
                      className={`form-control ${errors.companySiret && touched.companySiret ? 'is-invalid' : ''}`}
                      placeholder="12345678900012"
                    />
                    <ErrorMessage name="companySiret" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="companyAddress">Adresse postale *</label>
                    <Field
                      as="textarea"
                      name="companyAddress"
                      id="companyAddress"
                      className={`form-control ${errors.companyAddress && touched.companyAddress ? 'is-invalid' : ''}`}
                      rows={3}
                      placeholder="Adresse complète"
                    />
                    <ErrorMessage name="companyAddress" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="companyPhone">Téléphone *</label>
                    <Field
                      type="text"
                      name="companyPhone"
                      id="companyPhone"
                      className={`form-control ${errors.companyPhone && touched.companyPhone ? 'is-invalid' : ''}`}
                      placeholder="+33 1 00 00 00 00"
                    />
                    <ErrorMessage name="companyPhone" component="div" className="invalid-feedback" />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting || loading ? 'Enregistrement...' : 'Mettre à jour'}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        )}

        {/* ── Onglet Mot de passe ── */}
        {activeTab === 'password' && (
          <div className="profile-card">
            <h2>Changer le mot de passe</h2>

            <Formik
              initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
              validationSchema={PasswordSchema}
              onSubmit={handlePasswordSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="password-form">
                  <div className="form-group">
                    <label htmlFor="currentPassword">Mot de passe actuel</label>
                    <div className="password-field">
                      <Field
                        type={showPassword.current ? 'text' : 'password'}
                        name="currentPassword"
                        id="currentPassword"
                        className={`form-control ${errors.currentPassword && touched.currentPassword ? 'is-invalid' : ''}`}
                      />
                      <button type="button" className="password-toggle" onClick={() => toggleShowPassword('current')}>
                        <i className="material-icons">{showPassword.current ? 'visibility_off' : 'visibility'}</i>
                      </button>
                    </div>
                    <ErrorMessage name="currentPassword" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">Nouveau mot de passe</label>
                    <div className="password-field">
                      <Field
                        type={showPassword.new ? 'text' : 'password'}
                        name="newPassword"
                        id="newPassword"
                        className={`form-control ${errors.newPassword && touched.newPassword ? 'is-invalid' : ''}`}
                      />
                      <button type="button" className="password-toggle" onClick={() => toggleShowPassword('new')}>
                        <i className="material-icons">{showPassword.new ? 'visibility_off' : 'visibility'}</i>
                      </button>
                    </div>
                    <ErrorMessage name="newPassword" component="div" className="invalid-feedback" />
                    <small className="form-text text-muted">
                      Au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                    <div className="password-field">
                      <Field
                        type={showPassword.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        id="confirmPassword"
                        className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'is-invalid' : ''}`}
                      />
                      <button type="button" className="password-toggle" onClick={() => toggleShowPassword('confirm')}>
                        <i className="material-icons">{showPassword.confirm ? 'visibility_off' : 'visibility'}</i>
                      </button>
                    </div>
                    <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting || loading ? 'Changement...' : 'Changer le mot de passe'}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        )}
        {/* ── Onglet Notifications ── */}
        {activeTab === 'notifications' && (
          <div className="profile-card">
            <h2>Notifications email</h2>
            <p className="profile-section-desc">Choisissez les emails que vous souhaitez recevoir.</p>

            {emailNotifLoading ? (
              <div className="loading-state">Chargement...</div>
            ) : (
              <div className="notif-prefs">

                {/* Soumissions */}
                <div className="notif-group">
                  <div className="notif-group-header">
                    <div className="notif-group-info">
                      <span className="notif-group-label">Changement de statut de soumission</span>
                      <span className="notif-group-desc">Email reçu lorsque le statut de votre soumission est modifié.</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={getNotifPref('submission_status').enabled}
                        onChange={() => toggleNotifEnabled('submission_status')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  {getNotifPref('submission_status').enabled && (
                    <div className="notif-statuses">
                      <span className="notif-statuses-label">Déclencher pour :</span>
                      <div className="alert-statuses-grid">
                        {[
                          { value: 'approved', label: 'Approuvée' },
                          { value: 'rejected', label: 'Rejetée' },
                          { value: 'in_progress', label: 'En cours' }
                        ].map(s => (
                          <label key={s.value} className="status-checkbox">
                            <input
                              type="checkbox"
                              checked={(getNotifPref('submission_status').statuses || []).includes(s.value)}
                              onChange={() => toggleNotifStatus('submission_status', s.value)}
                            />
                            <span className={`status-badge ${s.value}`}>{s.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Commandes */}
                <div className="notif-group">
                  <div className="notif-group-header">
                    <div className="notif-group-info">
                      <span className="notif-group-label">Changement de statut de commande</span>
                      <span className="notif-group-desc">Email reçu lorsque le statut de votre commande est modifié.</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={getNotifPref('order_status').enabled}
                        onChange={() => toggleNotifEnabled('order_status')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  {getNotifPref('order_status').enabled && (
                    <div className="notif-statuses">
                      <span className="notif-statuses-label">Déclencher pour :</span>
                      <div className="alert-statuses-grid">
                        {[
                          { value: 'accepted', label: 'Acceptée' },
                          { value: 'in_progress', label: 'En cours' },
                          { value: 'completed', label: 'Terminée' },
                          { value: 'sent', label: 'Expédiée' },
                          { value: 'cancelled', label: 'Annulée' }
                        ].map(s => (
                          <label key={s.value} className="status-checkbox">
                            <input
                              type="checkbox"
                              checked={(getNotifPref('order_status').statuses || []).includes(s.value)}
                              onChange={() => toggleNotifStatus('order_status', s.value)}
                            />
                            <span className={`status-badge ${s.value}`}>{s.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {notifSaveSuccess && (
                  <div className="alert alert-success">Préférences sauvegardées.</div>
                )}

                <button
                  className="btn btn-primary"
                  onClick={handleSaveNotifications}
                  disabled={emailNotifSaving}
                >
                  {emailNotifSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            )}
          </div>
        )}
        {/* ── Onglet Vacances ── */}
        {activeTab === 'vacations' && (
          <div className="profile-card">
            <h2>Périodes de vacances</h2>
            <p className="profile-section-desc">Définissez vos périodes d'absence pour informer les équipes de vos disponibilités.</p>

            <form className="vacation-form" onSubmit={handleVacationSubmit}>
              <h3 className="vacation-form-title">
                {editingVacation ? 'Modifier une période' : 'Ajouter une période'}
              </h3>

              <div className="vacation-form-grid">
                <div className="form-group">
                  <label htmlFor="startDate">Date de début</label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-control"
                    value={vacationForm.startDate}
                    onChange={e => setVacationForm(prev => ({ ...prev, startDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endDate">Date de fin</label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-control"
                    value={vacationForm.endDate}
                    onChange={e => setVacationForm(prev => ({ ...prev, endDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group vacation-label-field">
                  <label htmlFor="vacLabel">Libellé (optionnel)</label>
                  <input
                    type="text"
                    id="vacLabel"
                    className="form-control"
                    placeholder="Ex : Congés d'été, RTT..."
                    value={vacationForm.label}
                    onChange={e => setVacationForm(prev => ({ ...prev, label: e.target.value }))}
                  />
                </div>
              </div>

              {vacationFormError && <div className="alert alert-danger">{vacationFormError}</div>}

              <div className="vacation-form-actions">
                <button type="submit" className="btn btn-primary" disabled={vacationSaving}>
                  {vacationSaving ? 'Enregistrement...' : editingVacation ? 'Modifier' : 'Ajouter'}
                </button>
                {editingVacation && (
                  <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                    Annuler
                  </button>
                )}
              </div>
            </form>

            {vacationSuccess && <div className="alert alert-success">{vacationSuccess}</div>}

            <div className="vacation-list">
              <h3 className="vacation-list-title">Mes périodes de vacances</h3>

              {vacationsLoading ? (
                <div className="loading-state">Chargement...</div>
              ) : vacations.length === 0 ? (
                <div className="vacation-empty">
                  <i className="material-icons">beach_access</i>
                  <p>Aucune période de vacances définie.</p>
                </div>
              ) : (
                <ul className="vacation-items">
                  {vacations.map(v => (
                    <li key={v.id} className="vacation-item">
                      <div className="vacation-item-icon">
                        <i className="material-icons">event</i>
                      </div>
                      <div className="vacation-item-info">
                        <span className="vacation-item-dates">
                          {formatDate(v.start_date)} → {formatDate(v.end_date)}
                        </span>
                        {v.label && <span className="vacation-item-label">{v.label}</span>}
                      </div>
                      <div className="vacation-item-actions">
                        <button
                          className="btn-icon"
                          title="Modifier"
                          onClick={() => handleEditVacation(v)}
                        >
                          <i className="material-icons">edit</i>
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          title="Supprimer"
                          onClick={() => handleDeleteVacation(v.id)}
                          disabled={vacationSaving}
                        >
                          <i className="material-icons">delete</i>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
